import {
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';

export type PairComparisonByFieldNameInput<TRow extends { fieldName?: string }> = {
	current: TRow[];
	previous: TRow[];
	hasComparison: boolean;
	matchField?: string;
	buildPreviousRow?: (previousRow: TRow | null, currentRow: TRow) => Record<string, any>;
};

export type PairComparisonByFieldNameResult = {
	current: any[];
	previous: any[];
};

/**
 * Pairs current- and previous-period breakdown rows by category label (default `fieldName`).
 *
 * Emits a row in each series for every category that appears in EITHER period. Categories
 * that only have current data get a synthetic zero previous row (the default behaviour);
 * categories that only have previous data get a synthetic zero current row so the chart
 * still draws a band for them. Without the second case, vendors / problem types with prior
 * spend but no current spend would silently disappear from the chart while still showing up
 * in the CSV — which is what the user reported on the spend-by-vendor view.
 */
export const pairComparisonByFieldName = <TRow extends { fieldName?: string }>({
	current,
	previous,
	hasComparison,
	matchField = 'fieldName',
	buildPreviousRow,
}: PairComparisonByFieldNameInput<TRow>): PairComparisonByFieldNameResult => {
	const effectiveBuildPreviousRow =
		buildPreviousRow ||
		((previousRow: TRow | null, currentRow: TRow) => ({
			...(previousRow || {}),
			[matchField]: currentRow && currentRow[matchField],
			fieldName: (currentRow && currentRow.fieldName) || (currentRow && currentRow[matchField]),
			subTotal: previousRow ? (previousRow as any).subTotal : 0,
		}));

	if (!hasComparison || !previous || previous.length === 0) {
		const currentTagged = (current || []).map((row) => ({
			...row,
			[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT,
		}));
		return { current: currentTagged, previous: [] };
	}

	const currentByField = new Map<string, TRow>();
	(current || []).forEach((row) => {
		if (row && row[matchField]) {
			currentByField.set(String(row[matchField]), row);
		}
	});

	const previousByField = new Map<string, TRow>();
	(previous || []).forEach((row) => {
		if (row && row[matchField]) {
			previousByField.set(String(row[matchField]), row);
		}
	});

	// Union of all category labels — insertion order keeps current categories first (in
	// whatever order the caller sorted them) and then appends previous-only categories.
	const orderedFieldNames: string[] = [];
	const seenFieldNames = new Set<string>();
	const pushFieldName = (rawValue: any) => {
		if (rawValue === null || rawValue === undefined) return;
		const key = String(rawValue);
		if (!key || seenFieldNames.has(key)) return;
		seenFieldNames.add(key);
		orderedFieldNames.push(key);
	};
	(current || []).forEach((row) => row && pushFieldName(row[matchField]));
	(previous || []).forEach((row) => row && pushFieldName(row[matchField]));

	const allCurrentRows: any[] = [];
	const allPreviousRows: any[] = [];

	orderedFieldNames.forEach((fieldName) => {
		const realCurrent = currentByField.get(fieldName);
		const realPrevious = previousByField.get(fieldName);

		if (realCurrent) {
			allCurrentRows.push({
				...realCurrent,
				[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT,
			});
		} else if (realPrevious) {
			// Synthetic zero current row so the chart still draws a band for previous-only
			// categories. Inherits structural fields from the previous row but zeros out the
			// value/percentage so no current bar paints.
			allCurrentRows.push({
				...(realPrevious as any),
				[matchField]: fieldName,
				subTotal: 0,
				percentageOfTotal: 0,
				[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT,
			});
		}

		// `buildPreviousRow` expects a current row to read structural fields off (e.g. fieldName
		// for label placement). Use the real current when available, otherwise a stub keyed by
		// the field name we're stitching.
		const baseCurrentForBuild: TRow =
			realCurrent ||
			({
				[matchField]: fieldName,
			} as unknown as TRow);
		allPreviousRows.push({
			...effectiveBuildPreviousRow(realPrevious || null, baseCurrentForBuild),
			[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_PREVIOUS,
		});
	});

	return { current: allCurrentRows, previous: allPreviousRows };
};
